import { Button, Modal, Popover, Segmented, Tooltip } from "antd";
import { message } from "utils/ciq-message";
import InlineEditTitle from "components/inline-edit-title";
import { CiqIconBox, CopyIcon } from "components/svg-icons";
import { useCallback, useContext, useMemo, useEffect, useState } from "react";
import "./submittal-detail-header.scss";
import ErrorBoundary from "components/error-boundary";
import { useHistory, useLocation, useParams } from "react-router";
import { ArrowLeftOutlined } from "@ant-design/icons";
import FeatureDuplicate from "components/feature-duplicate";
import { FeatureTypes } from "entity-app/constants";
import MicrovizDateBlock from "components/microviz/microviz-dateblock";
import {
  getUserFromParticipantType,
  transformDataFromWFInstanceMilestones
} from "components/microviz/microviz-data-transformations";
import SubmittalMicroVisualiser from "components/submittal-details/submittal-micro-viz";
import { DateUtils } from "utils/dateutils";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { MUTATION_REDISTRIBUTE_SUBMITTAL } from "services/graphQL/mutations";
import { CloseAndDistributeSubmittal } from "components/close-and-distribute";
import ArrowUpBox from "components/svg-icons/arrow-right-up-box-line";
import { ProjectContext } from "context/ProjectProvider";
import {
  EWorkflowStatusDataBlock,
  IntegrationLinkTooltipMsg
} from "../../../../constants";
import FeatureLogNavigator from "components/feature-log-navigator";
import { getSubmittalIntegrationConfig } from "pages/subscription-settings/integrations-tab-new/utils";
import type { FeatureLogNavReaderResult } from "hooks/use-feature-log-nav-reader";
import FeatureSubmittalRevision from "../components/submittal-revision";
import { useSubmmittialDetails } from "../hook/submittal-details-hook";

function FeatureHeader(
  props: Readonly<{
    featureInstanceData?: any;
    title: string;
    subTitle?: string;
    actions?: any;
    headerTitle?: string;
    number?: string;
    disabled: boolean;
    feature:
      | "SUBMITTALS"
      | "SUBMITTALS_UNTRACKED"
      | "SUBMITTALS_VOIDED"
      | "SUBMITTALS_DELETED";
    goBackToList: () => void;
    getTabIndex: (tab: string) => void;
    submittal: any;
    projectIntegrationConfig: any;
    isIntegrationMode: boolean;
    isAttachementFeatureEnabled?: boolean;
    setRevisionMode: any;
    submittalHeaderMap: any;
    tokenContents: any;
    setSubmittalViewState: any;
    gqlClientForProject: any;
    isRevisionMode?: boolean;
    updateFeatureData: {
      localFeatureInstanceData: any;
      setLocalFeatureInstanceData: (value: any) => void;
    };
    readOnlyMode?: boolean;
    navigator?: FeatureLogNavReaderResult;
  }>
) {
  const {
    featureInstanceData,
    actions,
    title,
    subTitle,
    headerTitle,
    number,
    disabled,
    feature,
    goBackToList,
    getTabIndex,
    submittal,
    projectIntegrationConfig,
    isIntegrationMode,
    isAttachementFeatureEnabled = true,
    setRevisionMode,
    submittalHeaderMap,
    tokenContents,
    setSubmittalViewState,
    gqlClientForProject,
    updateFeatureData: {
      localFeatureInstanceData,
      setLocalFeatureInstanceData
    },
    readOnlyMode,
    isRevisionMode = false,
    navigator
  } = props;
  const { projectId, submittalId } = useParams() as any;
  const history = useHistory();
  const location = useLocation();
  const tab = new URLSearchParams(location.search).get("tab");
  const [tabIndex, setTabIndex] = useState<string>(tab || "1");
  const [submittalRedistributeUsers, setSubmittalRedistributeUsers] = useState(
    []
  );
  const [submittalRedistributing, setSubmittalRedistributed] = useState(false);
  const { isProjectArchived } = useContext(ProjectContext);
  const [openDistributeModel, setOpenDistributeSubmittalModel] =
    useState(false);

  const [visible, setVisible] = useState(false);
  const [headerInfo, setHeaderInfo] = useState<{
    featureHeaderNumber?: string;
    featureHeaderTitle?: string;
  }>();

  const { cannotCreateSubmittal, submittalParticipants } =
    useSubmmittialDetails({
      featureInstanceData: localFeatureInstanceData,
      isRevisionMode
    });

  const updateHeaderInfo = async (headersInfo: any) => {
    setHeaderInfo({
      featureHeaderNumber: headersInfo?.number,
      featureHeaderTitle: headersInfo?.title
    });
    await actions.updateFeatureInstanceFields({
      title: headersInfo?.title,
      number: headersInfo?.number
    });
  };

  const [redistributeSubmittal] = useCIQMutation(
    MUTATION_REDISTRIBUTE_SUBMITTAL,
    {
      client: gqlClientForProject
    }
  );

  useEffect(() => {
    setHeaderInfo({
      featureHeaderNumber: number,
      featureHeaderTitle: headerTitle
    });
  }, [headerTitle, number]);

  const segmentOptions = useMemo(() => {
    const options = [{ label: "Details", value: "1" }];
    if (!isIntegrationMode && isAttachementFeatureEnabled)
      options.push({ label: "Attachments", value: "2" });

    return options;
  }, [isIntegrationMode, isAttachementFeatureEnabled]);

  const redistributeClosedSubmittal = async () => {
    setSubmittalRedistributed(true);
    const responseData: any = await redistributeSubmittal({
      variables: {
        submittalId: featureInstanceData?.id,
        toAddresses: submittalRedistributeUsers
      }
    });
    setSubmittalRedistributed(false);
    if (responseData?.data?.redistribute_submittal?.message) {
      message.success("Submittal redistributed successfully.");
    } else {
      message.error("Failed to redistribute submittal.");
    }
  };

  const dataForMicroviz = useCallback(() => {
    const designReviewerUser = submittalParticipants?.drReviewer;
    const gcReviewerUser = submittalParticipants?.gcReviewer;

    return {
      submittal_history: submittal?.submittal_status_history_details,
      design_reviewer_id: designReviewerUser?.user?.id,
      gc_reviewer_id: gcReviewerUser?.user?.id,
      vendorInfo: {
        designReviewerCompanyName: designReviewerUser?.user?.project_users
          ?.length
          ? designReviewerUser?.user?.project_users[0]?.subscription_vendor
              ?.name
          : "",
        responsibleContractorCompanyName:
          submittal?.responsible_contractor_vendor?.name
      }
    };
  }, [
    submittal?.responsible_contractor_vendor?.name,
    submittal?.submittal_status_history_details,
    submittalParticipants?.drReviewer,
    submittalParticipants?.gcReviewer
  ]);

  const distributedDate =
    localFeatureInstanceData?.feature_workflow_instance?.length &&
    localFeatureInstanceData?.feature_workflow_instance[4]
      ? localFeatureInstanceData?.feature_workflow_instance[4]?.actual_date
      : null;

  return (
    <div
      className="flex flex-col w-full bg-white px-6 py-4 space-y-2"
      data-testid="submittal-header-root"
    >
      <div className="flex justify-between items-center">
        <div className="flex space-x-2 items-center">
          <Button
            icon={<ArrowLeftOutlined />}
            className="text-one text-black !bg-transparent !border-0 px-0 !shadow-none"
            onClick={goBackToList}
            data-testid="submittal-header-back-button"
          />
          <CiqIconBox feature={feature} size={42} padding={0} />
          <div className="flex-col pl-1">
            <div className="flex items-center space-x-2">
              <div
                className="text-xl font-semibold"
                data-testid="submittal-header-title"
              >
                {title}
              </div>
            </div>
            {subTitle && (
              <div
                className="text-xs font-normal"
                data-testid="submittal-header-subtitle"
              >
                {subTitle}
              </div>
            )}
          </div>
          {!readOnlyMode && !!segmentOptions.length && (
            <Segmented
              shape="round"
              size="large"
              value={tabIndex}
              options={segmentOptions}
              onChange={(value) => {
                setTabIndex(value as string);
                getTabIndex(value as string);
                history.push({
                  pathname: `/project/${projectId}/submittals/${submittalId}`,
                  search: `?tab=${value}`,
                  state: location.state
                });
              }}
              data-testid="submittal-header-tab-segmented"
            />
          )}
          {navigator?.visible && (
            <FeatureLogNavigator
              visible={navigator.visible}
              currentIndex={navigator.currentIndex}
              total={navigator.total}
              onPrev={navigator.goPrev}
              onNext={navigator.goNext}
            />
          )}
        </div>
        {!isIntegrationMode &&
          submittal?.workflow_status ===
            EWorkflowStatusDataBlock.WF_COMPLETED && (
            <div className="flex space-x-2 items-center">
              <div
                className="font-semibold"
                data-testid="submittal-header-distributed-label"
              >
                Submittal Distributed: {DateUtils.format(distributedDate)}
              </div>
              {!readOnlyMode && (
                <div>
                  <Button
                    onClick={() => {
                      setOpenDistributeSubmittalModel(true);
                    }}
                    loading={submittalRedistributing}
                    disabled={submittalRedistributing || !!isProjectArchived}
                    data-testid="index-button-redistribute"
                  >
                    Redistribute
                  </Button>
                  <Modal
                    title="Redistribute Submittal"
                    width={500}
                    open={openDistributeModel}
                    okButtonProps={{
                      disabled: submittalRedistributeUsers?.length === 0
                    }}
                    onOk={() => {
                      if (submittalRedistributeUsers?.length > 0) {
                        setOpenDistributeSubmittalModel(false);
                        redistributeClosedSubmittal();
                      }
                    }}
                    onCancel={() => {
                      setOpenDistributeSubmittalModel(false);
                    }}
                    okText="Redistribute"
                    destroyOnHidden
                    data-testid="index-modal-redistribute"
                  >
                    <CloseAndDistributeSubmittal
                      submittalData={featureInstanceData}
                      submittalWorkflowResponses={[]}
                      commentVal=""
                      redistributeSubmittalToUsers={(selectedusers: any) => {
                        setSubmittalRedistributeUsers(selectedusers);
                      }}
                      submittalViewState={{
                        status: submittal?.workflow_status,
                        userType: tokenContents?.userType
                      }}
                      data-testid="submittal-header-redistribute-component"
                    />
                  </Modal>
                </div>
              )}
            </div>
          )}

        <div className="flex space-x-2 items-center">
          {!isIntegrationMode && !readOnlyMode && (
            <Popover
              open={!cannotCreateSubmittal && visible}
              placement="bottomRight"
              content={
                <ErrorBoundary>
                  <FeatureDuplicate
                    featureInstanceData={featureInstanceData}
                    actions={actions}
                    currentFeatureType={FeatureTypes.SUBMITTALS}
                    linkedSourcefeatureType={FeatureTypes.MATERIALS}
                    setVisible={setVisible}
                    visible={visible}
                    data-testid="submittal-header-duplicate-component"
                  />
                </ErrorBoundary>
              }
              trigger="click"
              onOpenChange={() => {
                if (!cannotCreateSubmittal) setVisible(!visible);
              }}
              destroyOnHidden
              data-testid="submittal-header-duplicate-popover"
            >
              <Tooltip data-testid="submittal-header-duplicate-tooltip">
                <Button
                  onClick={() => setVisible(true)}
                  disabled={cannotCreateSubmittal}
                  data-testid="index-button-duplicate"
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className=" text-[12px]"
                      data-testid="submittal-header-duplicate-text"
                    >
                      Duplicate
                    </div>
                    <CopyIcon />
                  </div>
                </Button>
              </Tooltip>
            </Popover>
          )}
          <FeatureSubmittalRevision
            featureInstanceData={featureInstanceData}
            localFeatureInstanceData={localFeatureInstanceData}
            setLocalFeatureInstanceData={setLocalFeatureInstanceData}
            submittal={submittal}
            isIntegrationMode={isIntegrationMode}
            setRevisionMode={setRevisionMode}
            submittalHeaderMap={submittalHeaderMap}
            tokenContents={tokenContents}
            setSubmittalViewState={setSubmittalViewState}
            data-testid="submittal-header-revision-component"
          />
          {localFeatureInstanceData?.redirect_url && (
            <Tooltip
              placement="right"
              title={
                // The link points at the submittal's source system, so the row
                // is resolved by system rather than taken from [0] — an OPC
                // schedule config sharing that array would otherwise name it.
                IntegrationLinkTooltipMsg[
                  getSubmittalIntegrationConfig(projectIntegrationConfig)
                    ?.integration
                    ?.system as keyof typeof IntegrationLinkTooltipMsg
                ] ?? ""
              }
              data-testid="submittal-header-integration-tooltip"
            >
              <Button
                className="icon-link-arrow button-spacing"
                icon={<ArrowUpBox />}
                color="default"
                type="link"
                onClick={() => {
                  window.open(localFeatureInstanceData?.redirect_url, "_blank");
                }}
                data-testid="submittal-header-integration-link-button"
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="flex py-2 md:px-16 lg:px-24 sm:px-4 items-center justify-center">
        <div className="flex w-full p-4 bg-ciq-gray-50 bg-opacity-60 items-center justify-between">
          <div
            className="flex items-center space-x-10"
            data-testid="submittal-header-inline-edit"
          >
            <ErrorBoundary>
              <InlineEditTitle
                isPermissionToEdit={disabled}
                headerInfo={headerInfo}
                updateHeaderInfo={updateHeaderInfo}
                feature={feature}
                data-testid="submittal-header-inline-edit-title"
              />
            </ErrorBoundary>
            <div className="flex ml-1">
              {submittalHeaderMap?.revision}:{" "}
              {localFeatureInstanceData?.revision ?? "0"}
            </div>
          </div>
          <div className="flex items-center space-x-10">
            {featureInstanceData && isIntegrationMode && (
              <div className="w-[260px] text-right">
                <ErrorBoundary>
                  <MicrovizDateBlock
                    submittalData={{
                      ...featureInstanceData,
                      revision: 0,
                      revision_date_blocks: []
                    }}
                    submittalRevisions={[]}
                    selectedRevision={0}
                    dataTransformFunction={
                      transformDataFromWFInstanceMilestones
                    }
                    userGetterFunction={getUserFromParticipantType}
                    data-testid="submittal-header-microviz-dateblock"
                  />
                </ErrorBoundary>
              </div>
            )}

            {featureInstanceData && !isIntegrationMode && (
              <div className="w-[260px] text-right">
                <ErrorBoundary>
                  <SubmittalMicroVisualiser
                    {...dataForMicroviz()}
                    titleBar
                    data-testid="submittal-header-microviz-visualiser"
                  />
                </ErrorBoundary>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeatureHeader;
